// Portrait compositor (spec-captain §Runtime): stacks atlas parts into a
// 48×48 offscreen canvas from a captain record. The atlas is fetched once
// and cached; composites are cached per seed. Until Agent A's
// public/captain/atlas.{json,png} lands, everything falls back to a
// deterministic procedural placeholder bust so the feature is testable
// today.

import { ATLAS_SLOT_COUNTS } from './seed.js';
import type { AtlasSlot, CaptainRecord } from './seed.js';
import { drawPixelCharacter } from './pixelchar.js';
import type { PortraitStyle } from '../types.js';

/** Multiply-tint ramp applied to the face-base layer only (spec-captain §Atlas).
 * The darkest tone stays readable — a near-black multiply makes faces drown. */
export const SKIN_TONES = ['#ffcd75', '#ef7d57', '#c97a5e', '#8a5a3b'] as const;

const ATLAS_JSON_URL = 'captain/atlas.json';
const ATLAS_PNG_URL = 'captain/atlas.png';
const CELL = 48;

// Back-to-front draw order: uniform under everything, headwear on top.
const DRAW_ORDER: readonly AtlasSlot[] = [
  'uniform', 'face', 'nose', 'mouth', 'eyes', 'brows', 'mark', 'beard', 'hair', 'headwear'
];

/** Anchor metrics as shipped in atlas.json (eye/mouth lines, jaw width). */
interface AtlasAnchors {
  eyeY?: number;
  mouthY?: number;
  jawW?: number;
}

interface AtlasItem {
  id: string;
  col: number;
  anchors?: AtlasAnchors;
}

interface AtlasSlotDef {
  count: number;
  row: number;
  /** Slot-wide default anchors. */
  anchors?: AtlasAnchors;
  /** Per-part entries with optional anchor overrides. */
  items?: AtlasItem[];
}

interface AtlasDef {
  cell: number;
  slots: Partial<Record<AtlasSlot, AtlasSlotDef>>;
}

interface LoadedAtlas {
  def: AtlasDef;
  image: CanvasImageSource;
}

/** Item anchor with slot-default fallback; empty object when none exist. */
function anchorOf(slotDef: AtlasSlotDef | undefined, index: number): AtlasAnchors {
  return slotDef?.items?.[index]?.anchors ?? slotDef?.anchors ?? {};
}

let atlasPromise: Promise<LoadedAtlas | null> | null = null;
// Keyed `${seed}:${style}` — the same seed renders differently per style.
const compositeCache = new Map<string, HTMLCanvasElement>();
let tintScratch: HTMLCanvasElement | null = null;

/** Fetch + decode the atlas once. Resolves to null when the atlas is absent. */
export function loadAtlas(): Promise<LoadedAtlas | null> {
  if (!atlasPromise) atlasPromise = fetchAtlas();
  return atlasPromise;
}

async function fetchAtlas(): Promise<LoadedAtlas | null> {
  try {
    const res = await fetch(ATLAS_JSON_URL);
    if (!res.ok) return null;
    const def = (await res.json()) as AtlasDef;
    if (!def || typeof def.cell !== 'number' || def.cell <= 0 || !def.slots) return null;
    const image = new Image();
    image.src = ATLAS_PNG_URL;
    await image.decode();
    // Despeckle once at load: AI generation leaves confetti blobs (1–5 px
    // islands) around the parts; they read as noise on every composite.
    return { def, image: cleanAtlasImage(image) };
  } catch {
    // Missing json/png, malformed json — the placeholder bust covers it.
    return null;
  }
}

/** Remove isolated alpha blobs smaller than 6 px from every atlas cell. */
function cleanAtlasImage(image: HTMLImageElement): HTMLCanvasElement {
  const w = image.naturalWidth;
  const h = image.naturalHeight;
  const src = document.createElement('canvas');
  src.width = w;
  src.height = h;
  const sctx = src.getContext('2d')!;
  sctx.drawImage(image, 0, 0);
  const cols = Math.floor(w / CELL);
  const rows = Math.floor(h / CELL);
  for (let cy = 0; cy < rows; cy++) {
    for (let cx = 0; cx < cols; cx++) {
      despeckleCell(sctx, cx * CELL, cy * CELL, CELL);
    }
  }
  return src;
}

const DESPECKLE_MIN_PX = 6;

function despeckleCell(ctx: CanvasRenderingContext2D, ox: number, oy: number, size: number): void {
  const data = ctx.getImageData(ox, oy, size, size);
  const px = data.data;
  const n = size * size;
  const solid = new Uint8Array(n);
  for (let i = 0; i < n; i++) solid[i] = px[i * 4 + 3] > 0 ? 1 : 0;

  // 4-neighbor connected components.
  const comp = new Int32Array(n).fill(-1);
  const sizes: number[] = [];
  const stack: number[] = [];
  for (let i = 0; i < n; i++) {
    if (!solid[i] || comp[i] !== -1) continue;
    const id = sizes.length;
    let count = 0;
    comp[i] = id;
    stack.push(i);
    while (stack.length > 0) {
      const p = stack.pop()!;
      count++;
      const x = p % size;
      const y = (p / size) | 0;
      if (x > 0 && solid[p - 1] && comp[p - 1] === -1) { comp[p - 1] = id; stack.push(p - 1); }
      if (x < size - 1 && solid[p + 1] && comp[p + 1] === -1) { comp[p + 1] = id; stack.push(p + 1); }
      if (y > 0 && solid[p - size] && comp[p - size] === -1) { comp[p - size] = id; stack.push(p - size); }
      if (y < size - 1 && solid[p + size] && comp[p + size] === -1) { comp[p + size] = id; stack.push(p + size); }
    }
    sizes.push(count);
  }

  // Keep the main mass; erase small islands (generation confetti).
  let largest = 0;
  for (let i = 1; i < sizes.length; i++) {
    if (sizes[i]! > sizes[largest]!) largest = i;
  }
  for (let i = 0; i < n; i++) {
    const c = comp[i]!;
    if (c !== -1 && c !== largest && sizes[c]! < DESPECKLE_MIN_PX) {
      px[i * 4 + 3] = 0;
    }
  }
  ctx.putImageData(data, ox, oy);
}

/**
 * Composite the captain's 48×48 portrait. Style 'atlas' (default) uses the
 * atlas when available, otherwise the procedural placeholder bust; style
 * 'pixelchar' renders the seeded pixel character (captain/pixelchar.ts) and
 * never touches the atlas. Cached per seed+style — the returned canvas is
 * shared, treat it as read-only.
 */
export async function compositeCaptain(
  record: CaptainRecord,
  style: PortraitStyle = 'atlas'
): Promise<HTMLCanvasElement> {
  const key = `${record.seed}:${style}`;
  const cached = compositeCache.get(key);
  if (cached) return cached;
  let canvas: HTMLCanvasElement;
  if (style === 'pixelchar') {
    canvas = drawPixelCharBust(record);
  } else {
    const atlas = await loadAtlas();
    canvas = atlas ? drawFromAtlas(record, atlas) : drawPlaceholderBust(record);
  }
  compositeCache.set(key, canvas);
  return canvas;
}

/** Drop atlas + composite caches (dev hot-reload / tests). */
export function clearCaptainCaches(): void {
  atlasPromise = null;
  compositeCache.clear();
}

function drawFromAtlas(record: CaptainRecord, atlas: LoadedAtlas): HTMLCanvasElement {
  const cell = atlas.def.cell;
  const canvas = document.createElement('canvas');
  canvas.width = cell;
  canvas.height = cell;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;

  const faceIndex = clampIndex(record.parts.face, atlas.def.slots.face?.count ?? ATLAS_SLOT_COUNTS.face);
  const faceAnchor = anchorOf(atlas.def.slots.face, faceIndex);
  const tone = SKIN_TONES[record.skinTone % SKIN_TONES.length]!;

  for (const slot of DRAW_ORDER) {
    const slotDef = atlas.def.slots[slot];
    if (!slotDef || slotDef.count <= 0) continue;
    const index = clampIndex(record.parts[slot], slotDef.count);
    const sx = index * cell;
    const sy = slotDef.row * cell;
    // Align feature anchors to the face base's lines (eye/mouth rows only).
    let dx = 0;
    let dy = 0;
    if (slot !== 'face') {
      const partAnchor = anchorOf(slotDef, index);
      if (
        (slot === 'eyes' || slot === 'brows') &&
        faceAnchor.eyeY !== undefined &&
        partAnchor.eyeY !== undefined
      ) {
        dy = Math.round(faceAnchor.eyeY - partAnchor.eyeY);
      } else if (
        slot === 'mouth' &&
        faceAnchor.mouthY !== undefined &&
        partAnchor.mouthY !== undefined
      ) {
        dy = Math.round(faceAnchor.mouthY - partAnchor.mouthY);
      }
    }
    if (slot === 'face') {
      ctx.drawImage(tintedFaceBase(atlas.image, sx, sy, cell, tone), dx, dy);
    } else {
      ctx.drawImage(atlas.image, sx, sy, cell, cell, dx, dy, cell, cell);
    }
  }
  return canvas;
}

function clampIndex(index: number, count: number): number {
  return Math.max(0, Math.min(count - 1, index));
}

// Skin tone as a multiply pass over the face-base layer, masked back to the
// layer's alpha so the tint never bleeds into the transparent background.
function tintedFaceBase(
  image: CanvasImageSource,
  sx: number,
  sy: number,
  cell: number,
  tone: string
): HTMLCanvasElement {
  if (!tintScratch || tintScratch.width !== cell) {
    tintScratch = document.createElement('canvas');
    tintScratch.width = cell;
    tintScratch.height = cell;
  }
  const ctx = tintScratch.getContext('2d')!;
  ctx.globalCompositeOperation = 'source-over';
  ctx.clearRect(0, 0, cell, cell);
  ctx.drawImage(image, sx, sy, cell, cell, 0, 0, cell, cell);
  ctx.globalCompositeOperation = 'multiply';
  ctx.fillStyle = tone;
  ctx.fillRect(0, 0, cell, cell);
  ctx.globalCompositeOperation = 'destination-in';
  ctx.drawImage(image, sx, sy, cell, cell, 0, 0, cell, cell);
  ctx.globalCompositeOperation = 'source-over';
  return tintScratch;
}

// ── Pixelchar bust ────────────────────────────────────────────────────────
// The seeded pixel character drawn at its native grid and scaled up to fill
// the 48×48 cell (nearest neighbor); the background stays transparent.

function drawPixelCharBust(record: CaptainRecord): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = CELL;
  canvas.height = CELL;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;
  const native = document.createElement('canvas');
  drawPixelCharacter(record.seed, native);
  ctx.drawImage(native, 0, 0, CELL, CELL);
  return canvas;
}

// ── Procedural placeholder bust ─────────────────────────────────────────
// Geometric 48×48 bust drawn from the record alone (no rng), so silhouette
// and colors shift with the seed until the real atlas replaces it.

const PLACEHOLDER_HAIR = ['#2b2b3a', '#5a3a28', '#8a5a3b', '#d8b36a', '#b13e53', '#3f6f5e', '#7a7a8c', '#e8e4d8'];
const PLACEHOLDER_UNIFORM = ['#1e5b7a', '#2f4a6e', '#4a2e5e', '#5e2e3a'];
const PLACEHOLDER_DARK = '#1a1420';

/** Deterministic geometric bust from the record; the atlas-missing fallback. */
export function drawPlaceholderBust(record: CaptainRecord): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = CELL;
  canvas.height = CELL;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;
  const p = record.parts;
  const skin = SKIN_TONES[record.skinTone % SKIN_TONES.length]!;
  const hair = PLACEHOLDER_HAIR[p.hair % PLACEHOLDER_HAIR.length]!;

  // Uniform shoulders.
  ctx.fillStyle = PLACEHOLDER_UNIFORM[p.uniform % PLACEHOLDER_UNIFORM.length]!;
  ctx.fillRect(8, 40, 32, 8);
  ctx.fillRect(14, 36, 20, 5);
  // Collar flash in Lumensurge gold.
  ctx.fillStyle = '#ffd36b';
  ctx.fillRect(22, 37, 4, 3);

  // Head — the face slot nudges width/height so silhouettes differ at 48px.
  const headW = 16 + p.face * 2;
  const headH = 20 + (p.face % 2) * 2;
  const headX = Math.round((CELL - headW) / 2);
  const headY = 24 - Math.round(headH / 2);
  ctx.fillStyle = skin;
  ctx.fillRect(headX, headY, headW, headH);

  // Hair: top strip of varying height, taller styles get side columns.
  const hairH = 3 + (p.hair % 4);
  ctx.fillStyle = hair;
  ctx.fillRect(headX - 1, headY - 1, headW + 2, hairH);
  if (p.hair >= 5) {
    ctx.fillRect(headX - 1, headY - 1, 3, hairH + 6);
    ctx.fillRect(headX + headW - 2, headY - 1, 3, hairH + 6);
  }

  // Headwear sits over the hair; index 0 is bare-headed.
  ctx.fillStyle = PLACEHOLDER_DARK;
  if (p.headwear === 1) {
    ctx.fillRect(headX - 1, headY - 1, headW + 2, 2); // band
  } else if (p.headwear === 2) {
    ctx.fillRect(headX - 1, headY - 4, headW + 2, 5); // cap
  } else if (p.headwear === 3) {
    ctx.fillRect(headX - 1, headY - 4, headW + 2, 5); // peaked cap + brim
    ctx.fillRect(headX + headW - 2, headY, 6, 2);
  } else if (p.headwear === 4) {
    ctx.fillStyle = hair; // hood frames the face in the hair color
    ctx.fillRect(headX - 2, headY - 3, headW + 4, 4);
    ctx.fillRect(headX - 2, headY - 3, 3, headH + 2);
    ctx.fillRect(headX + headW - 1, headY - 3, 3, headH + 2);
  }

  // Eyes: spread and size vary with the eyes slot.
  const eyeY = headY + 8;
  const spread = 4 + (p.eyes % 3);
  const eyeSize = 2 + (p.eyes % 2);
  const midX = headX + Math.floor(headW / 2);
  ctx.fillStyle = PLACEHOLDER_DARK;
  ctx.fillRect(midX - spread - eyeSize + 1, eyeY, eyeSize, eyeSize);
  ctx.fillRect(midX + spread, eyeY, eyeSize, eyeSize);

  // Brows: length/tilt vary with the brows slot.
  const browLen = 4 + (p.brows % 2) * 2;
  const browY = eyeY - 3 + (p.brows % 2);
  ctx.fillRect(midX - spread - browLen + 2, browY, browLen, 1);
  ctx.fillRect(midX + spread - 1, browY, browLen, 1);

  // Nose.
  ctx.fillRect(midX - 1, eyeY + 3, 2, 2 + (p.nose % 3));

  // Mouth: width varies with the mouth slot.
  const mouthW = 4 + (p.mouth % 4);
  ctx.fillRect(midX - Math.floor(mouthW / 2), headY + headH - 5, mouthW, 1);

  // Beard: index 0 is clean-shaven.
  if (p.beard > 0) {
    ctx.fillStyle = hair;
    ctx.fillRect(headX + 2, headY + headH - 3, headW - 4, 2 + p.beard);
  }

  // Distinguishing mark: index 0 is unmarked, the rest place a dark pixel
  // scar/mole on a cheek or the forehead.
  if (p.mark > 0) {
    ctx.fillStyle = PLACEHOLDER_DARK;
    const markX = p.mark % 2 === 0 ? headX + 3 : headX + headW - 5;
    const markY = p.mark <= 2 ? eyeY + 3 : headY + 4;
    ctx.fillRect(markX, markY, 2, 2);
  }
  return canvas;
}
